//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React from 'react';
import { communicationStyle } from '../../styles/communicationStyle';
import { useTranslation } from 'react-i18next';
import withZoom from '../Zoom/withZoom';
import { isSafeRedirectUrl } from '../../utils/validateUrlUtils';

interface SolutionCardProps {
  title: string;
  text: string;
  svgContent: React.ReactNode;
  buttonText: string;
  buttonLink: string;
  isButtonDisabled?: boolean;
  isZoomedIn?: boolean;
  isExternal?: boolean;
}

function getColorByTitle(title: string): string {
  switch (title.toLowerCase()) {
    case 'plan':
      return '#2EC0CB';
    case 'test':
      return '#5C6FCB';
    case 'deploy':
      return '#6FAFFD';
    case 'measure':
      return '#C7D540';
    case 'control':
      return '#D694F9';
    case 'release':
      return '#FFC766';
    case 'code':
      return '#FF8A00';
    case 'build':
      return '#57b946';
    default:
      return 'gray';
  }
}

function SolutionCard({
  title,
  text,
  svgContent,
  buttonText,
  buttonLink,
  isButtonDisabled = false,
  isZoomedIn,
  isExternal = false,
}: SolutionCardProps) {
  const { t } = useTranslation();
  const colorBarStyle = {
    ...communicationStyle.solutionCardColorBar,
    backgroundColor: getColorByTitle(title),
  };
  const buttonStyle = {
    ...communicationStyle.solutionCardButton,
    cursor: isButtonDisabled ? 'not-allowed' : 'pointer',
    ...(buttonText === t("Let's go to ") + title &&
      communicationStyle.letsGoButton),
    ...(buttonText === t('Purchase ') + title &&
      communicationStyle.purchaseButton),
  };

  const handleClick = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    event.preventDefault();
    if (isButtonDisabled) return;
    if (isExternal) {
      // External URLs come from server-side config (trusted); only require https.
      if (buttonLink.startsWith('https://')) {
        window.open(buttonLink, '_blank', 'noopener,noreferrer');
      } else {
        console.error('Blocked non-https external link:', buttonLink);
      }
    } else if (isSafeRedirectUrl(buttonLink)) {
      window.open(buttonLink, '_blank', 'noopener,noreferrer');
    } else {
      console.error('Blocked unsafe link:', buttonLink);
    }
  };

  return (
    <div
      id="id-solution-card"
      data-testid="solution-card"
      style={{
        ...communicationStyle.solutionCard,
        ...(isZoomedIn
          ? { width: '65vh', height: '300px' }
          : { width: '35vh', height: '250px' }),
        flexDirection: 'column',
      }}
    >
      <div
        id="id-solution-card-content-wrapper"
        data-testid="solution-card-content-wrapper"
      >
        <div
          id="id-solution-card-color-bar"
          data-testid="solution-card-color-bar"
          style={colorBarStyle}
        ></div>
        <div
          id="id-solution-card-svg"
          data-testid="solution-card-svg"
          style={communicationStyle.solutionCardSvg}
        >
          {svgContent}
        </div>
        <span
          id="id-solution-card-title"
          data-testid="solution-card-title"
          style={communicationStyle.solutionCardTitle}
        >
          {title}
        </span>
        <p style={communicationStyle.solutionCardText}>{text}</p>
      </div>
      <div
        id="id-solution-card-button-container"
        data-testid="solution-card-button-container"
        style={{
          display: 'flex',
          justifyContent: 'left',
          alignItems: 'flex-end',
        }}
      >
        <button
          style={buttonStyle}
          onClick={handleClick}
          disabled={isButtonDisabled}
          role="link"
          aria-label={buttonText}
        >
          <span
            id="id-solution-card-button-text"
            data-testid="solution-card-button-text"
            role="tooltip"
            aria-hidden="true"
          >
            {buttonText}
          </span>
        </button>
      </div>
    </div>
  );
}

export default withZoom(SolutionCard, 200);
